// UPLOAD-ANYWHERE-1: full-screen drop target for logged-in users.
// An in-app drag (timeline reorder, text, links) does not include "Files",
// so the overlay stays hidden and those drags keep working.
// On a project page the drop opens BulkUploadWizard for that project.
// Anywhere else the user picks an existing project. Creating a project
// from the drop is a follow-up (the /wizard hand-off).
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { collectDroppedFiles } from "../lib/collectDroppedFiles";

export function dragHasFiles(event) {
  const types = event && event.dataTransfer && event.dataTransfer.types;
  if (!types) return false;
  if (typeof types.contains === "function" && types.contains("Files")) return true;
  if (typeof types.includes === "function" && types.includes("Files")) return true;
  try {
    return Array.from(types).indexOf("Files") !== -1;
  } catch {
    return false;
  }
}

export function projectIdFromPath(pathname) {
  const match = /^\/project\/([^/]+)$/.exec(pathname || "");
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function QueuedWizard({ wizard, onClose, onComplete }) {
  // Loaded only when a drop opens the wizard, so the app shell does not
  // pull the bulk upload dialog into the first page render.
  const BulkUploadWizard = require("./BulkUploadWizard").default;
  return (
    <BulkUploadWizard
      projectId={wizard.projectId}
      initialFiles={wizard.files}
      queueToken={wizard.token}
      onClose={onClose}
      onComplete={onComplete}
    />
  );
}

function ProjectPicker({ files, onChoose, onClose }) {
  const [projects, setProjects] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/projects").then((res) => {
      if (!cancelled) setProjects(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {
      if (!cancelled) {
        setFailed(true);
        setProjects([]);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="project-picker" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-slate-800">Choose a project</h2>
        <p className="mt-1 text-sm text-slate-500">
          {files.length} file{files.length === 1 ? "" : "s"} ready to upload
        </p>
        {projects == null && <p className="mt-3 text-sm text-slate-400">Loading projects...</p>}
        {failed && <p className="mt-3 text-sm text-red-600">Could not load projects</p>}
        <ul className="mt-4 space-y-2">
          {(projects || []).map((project) => (
            <li key={project.id}>
              <button
                type="button"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left text-sm font-medium text-slate-800 hover:border-blue-400 hover:bg-blue-50"
                onClick={() => onChoose(project.id)}
              >
                {project.name || "Untitled project"}
              </button>
            </li>
          ))}
        </ul>
        {projects && projects.length === 0 && !failed && (
          <p className="mt-3 text-sm text-slate-500">No projects yet.</p>
        )}
        <div className="mt-4 flex justify-end">
          <button type="button" className="text-sm text-slate-500" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function GlobalDropZone() {
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const location = useLocation();
  const [overlay, setOverlay] = useState(false);
  const [wizard, setWizard] = useState(null);
  const [picker, setPicker] = useState(null);
  const pathnameRef = useRef(location.pathname);
  const wizardRef = useRef(null);
  const routeDropRef = useRef(async () => {});
  pathnameRef.current = location.pathname;
  wizardRef.current = wizard;

  routeDropRef.current = async (event) => {
    const target = event.target;
    if (target && typeof target.closest === "function" && target.closest("[data-bulk-dropzone]")) {
      return;
    }
    let files = [];
    try {
      files = await collectDroppedFiles(event.dataTransfer);
    } catch {
      toast.error("Could not read the dropped folder");
      return;
    }
    if (!files.length) return;
    const token = `${Date.now()}-${Math.random()}`;
    const open = wizardRef.current;
    if (open) {
      setPicker(null);
      setWizard({ projectId: open.projectId, files, token });
      return;
    }
    const projectId = projectIdFromPath(pathnameRef.current);
    if (projectId) {
      setPicker(null);
      setWizard({ projectId, files, token });
      return;
    }
    setPicker({ files, token });
  };

  useEffect(() => {
    if (!signedIn) return undefined;
    let depth = 0;

    const onEnter = (event) => {
      if (!dragHasFiles(event)) return;
      depth += 1;
      setOverlay(true);
    };
    const onLeave = (event) => {
      if (!dragHasFiles(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setOverlay(false);
    };
    const onOver = (event) => {
      if (!dragHasFiles(event)) return;
      event.preventDefault();
    };
    const onDrop = (event) => {
      if (!dragHasFiles(event)) return;
      event.preventDefault();
      depth = 0;
      setOverlay(false);
      routeDropRef.current(event);
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop, true);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop, true);
    };
  }, [signedIn]);

  if (!signedIn) return null;

  return (
    <>
      {overlay && (
        <div
          data-testid="global-drop-overlay"
          className="pointer-events-none fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60"
        >
          <div className="rounded-2xl bg-white px-10 py-8 text-center shadow-xl">
            <p className="text-2xl font-semibold text-slate-800">Drop to upload</p>
          </div>
        </div>
      )}
      {picker && !wizard && (
        <ProjectPicker
          key={picker.token}
          files={picker.files}
          onChoose={(projectId) => {
            const files = picker.files;
            setPicker(null);
            setWizard({ projectId, files, token: `pick-${projectId}` });
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {wizard && (
        <QueuedWizard
          key={wizard.projectId}
          wizard={wizard}
          onClose={() => setWizard(null)}
          onComplete={() => {
            window.dispatchEvent(new CustomEvent("stratalogy:bulk-upload-complete"));
            setWizard(null);
          }}
        />
      )}
    </>
  );
}
